import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Filters } from '../common/ui/types';
import type { State, IssueId, PageNumber, IssueDetailsType } from './types';

export const ArchiveIssueTableActions = {
	setIssuesInCurrentPage:
		(issues: string[]) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				issuesInCurrentPage: issues,
			});
		},
	setProjectId:
		(projectId: string) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				projectId,
			});
		},
	setLoading:
		(isLoading: boolean) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				isLoading,
			});
		},

	updateIssueDetails: (issueDetails: IssueDetailsType) => {
		return ({ setState }: StoreActionApi<State>) => {
			setState({
				issueDetails: { ...issueDetails },
			});
		};
	},

	selectIssue:
		({ issueId, pageNumber }: { issueId: IssueId; pageNumber: PageNumber }) =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { selectedIssues, numberOfSelectedIssues } = getState();

			const isSelected = selectedIssues[pageNumber]?.includes(issueId);

			if (!isSelected) {
				const updatedSelectedIssuesForPage = selectedIssues[pageNumber]
					? [...selectedIssues[pageNumber], issueId]
					: [issueId];

				setState({
					selectedIssues: {
						...selectedIssues,
						[pageNumber]: updatedSelectedIssuesForPage,
					},
					numberOfSelectedIssues: numberOfSelectedIssues + 1,
				});
			}
		},

	deselectIssue:
		({ issueId, pageNumber }: { issueId: IssueId; pageNumber: PageNumber }) =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { selectedIssues, numberOfSelectedIssues } = getState();

			const isSelected = selectedIssues[pageNumber]?.includes(issueId);

			if (isSelected) {
				const updatedSelectedIssuesForPage = selectedIssues[pageNumber].filter(
					(id) => id !== issueId,
				);

				setState({
					selectedIssues: {
						...selectedIssues,
						[pageNumber]: updatedSelectedIssuesForPage,
					},
					numberOfSelectedIssues: numberOfSelectedIssues - 1,
				});
			}
		},

	selectAllIssuesInCurrentPage:
		() =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { selectedIssues, issuesInCurrentPage, numberOfSelectedIssues, currentPage } =
				getState();
			const updatedSelectedIssuesForPage: IssueId[] = [];
			issuesInCurrentPage.forEach((issueId) => {
				updatedSelectedIssuesForPage.push(issueId);
			});

			const updatedNumberOfSelectedIssues = numberOfSelectedIssues + issuesInCurrentPage.length;

			setState({
				selectedIssues: {
					...selectedIssues,
					[currentPage]: updatedSelectedIssuesForPage,
				},
				numberOfSelectedIssues: updatedNumberOfSelectedIssues,
			});
		},

	clearSelectionsInCurrentPage:
		() =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { numberOfSelectedIssues, selectedIssues, currentPage } = getState();

			const selectedIssuesForCurrentPage = selectedIssues[currentPage];
			const numberOfSelectedIssuesOnCurrentPage = selectedIssuesForCurrentPage?.length || 0;

			const updatedselectedIssuesInPage = {
				...selectedIssues,
				[currentPage]: [],
			};

			setState({
				selectedIssues: updatedselectedIssuesInPage,
				numberOfSelectedIssues: numberOfSelectedIssues - numberOfSelectedIssuesOnCurrentPage,
			});
		},

	clearAllSelections:
		() =>
		({ setState, getState }: StoreActionApi<State>) => {
			const state = getState();

			setState({
				...state,
				selectedIssues: {},
				numberOfSelectedIssues: 0,
			});
		},

	addIssueInCurrentPage:
		(issueId: IssueId) =>
		({ setState, getState }: StoreActionApi<State>) => {
			const { issuesInCurrentPage } = getState();
			const isIssueInCurrentPage = issuesInCurrentPage.includes(issueId);
			if (!isIssueInCurrentPage) {
				setState({
					issuesInCurrentPage: [...issuesInCurrentPage, issueId],
				});
			}
		},

	clearIssuesInCurrentPage:
		() =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				issuesInCurrentPage: [],
			});
		},

	setCurrentPage:
		(pageNumber: number) =>
		({ setState }: StoreActionApi<State>) => {
			setState({ currentPage: pageNumber });
		},

	setFilters:
		(filterValue: Filters | undefined | {}) =>
		({ setState }: StoreActionApi<State>) => {
			const updatedFilterValue = { ...filterValue };
			Object.keys(updatedFilterValue).forEach((key) => {
				const value = updatedFilterValue[key];
				if (!Array.isArray(value) && value?.value && Object.keys(value.value).length === 0) {
					delete updatedFilterValue[key];
				}
			});
			setState({
				filters: updatedFilterValue,
				refetchArchivesIssuesData: true,
			});
		},
	setSearchBy:
		(searchBy: string) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				searchBy,
				refetchArchivesIssuesData: true,
			});
		},
	setArchivesIssueDataPresent:
		(archivesIssueDataPresent: boolean) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				archivesIssueDataPresent,
			});
		},
	setRefetchArchivesIssuesData:
		(refetchArchivesIssuesData: boolean) =>
		({ setState }: StoreActionApi<State>) => {
			setState({
				refetchArchivesIssuesData,
			});
		},
};

export type Actions = typeof ArchiveIssueTableActions;
